import type { HttpClient } from "./types";

export interface CartReconcileRequest {
    lines: Array<{
        productId: string;
        variantId: string;
        quantity: number;
        minimumQuantity: number;
    }>;
}

export interface CanonicalCartLine {
    productId: string;
    variantId: string;
    name: string;
    unitPrice: number;
    acceptedQuantity: number;
    stock?: number;
    available: boolean;
    allowWithoutStock: boolean;
    unitMeasureCode: number;
    unitLabel: string;
    cantDecimal: number;
    minimumQuantity: number;
    image: string;
    attributes: Array<{ name: string; value: string }>;
}

export type CartReconciliationChangeType = "QUANTITY_ADJUSTED" | "REMOVED";

export interface CartReconciliationChange {
    type: CartReconciliationChangeType;
    productId: string;
    variantId: string;
    previousQuantity?: number;
    currentQuantity?: number;
    reason: string;
}

export interface CartReconcileResponse {
    pricingContext: string;
    lines: CanonicalCartLine[];
    changes: CartReconciliationChange[];
}

interface CartReconcileApiLine {
    uniIdProducto: string;
    uniIdProductoVariante: string;
    vchNombre: string;
    decPrecioUnitario: number;
    decCantidadAceptada: number;
    decStock?: number | null;
    bitDisponible: boolean;
    bitPermiteOrdenSinStock: boolean;
    tinCodUnidadMedida: number;
    vchUnidadMedida: string;
    tinCantDecimal: number;
    decCantidadMinima: number;
    vchUrlImagen: string;
    lstAtributos: Array<{
        vchNombre: string;
        vchValor: string;
    }>;
}

interface CartReconcileApiChange {
    vchTipo: "CANTIDAD_AJUSTADA" | "NO_DISPONIBLE";
    uniIdProducto: string;
    uniIdProductoVariante: string;
    decCantidadAnterior?: number | null;
    decCantidadNueva?: number | null;
    vchMotivo: string;
}

interface CartReconcileApiResponse {
    vchContextoPrecio: string;
    lstLineas: CartReconcileApiLine[];
    lstCambios: CartReconcileApiChange[];
}

export const createCartService = (httpClient: HttpClient) => ({
    reconcile: async (request: CartReconcileRequest): Promise<CartReconcileResponse> => {
        const response = await httpClient.post<CartReconcileApiResponse>(
            "/tienda/carrito/reconciliar",
            {
                lstLineas: request.lines.map((line) => ({
                    uniIdProducto: line.productId,
                    uniIdProductoVariante: line.variantId,
                    decCantidad: line.quantity,
                    decCantidadMinima: line.minimumQuantity,
                })),
            },
        );

        return {
            pricingContext: response.vchContextoPrecio,
            lines: response.lstLineas.map((line) => ({
                productId: line.uniIdProducto,
                variantId: line.uniIdProductoVariante,
                name: line.vchNombre,
                unitPrice: line.decPrecioUnitario,
                acceptedQuantity: line.decCantidadAceptada,
                ...(line.decStock == null ? {} : { stock: line.decStock }),
                available: line.bitDisponible,
                allowWithoutStock: line.bitPermiteOrdenSinStock,
                unitMeasureCode: line.tinCodUnidadMedida,
                unitLabel: line.vchUnidadMedida,
                cantDecimal: line.tinCantDecimal,
                minimumQuantity: line.decCantidadMinima,
                image: line.vchUrlImagen,
                attributes: line.lstAtributos.map((attribute) => ({
                    name: attribute.vchNombre,
                    value: attribute.vchValor,
                })),
            })),
            changes: response.lstCambios.map((change) => ({
                type: change.vchTipo === "CANTIDAD_AJUSTADA" ? "QUANTITY_ADJUSTED" : "REMOVED",
                productId: change.uniIdProducto,
                variantId: change.uniIdProductoVariante,
                ...(change.decCantidadAnterior == null
                    ? {}
                    : { previousQuantity: change.decCantidadAnterior }),
                ...(change.decCantidadNueva == null
                    ? {}
                    : { currentQuantity: change.decCantidadNueva }),
                reason: change.vchMotivo,
            })),
        };
    },
});
